import React, { useRef, useEffect, useState, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import ParticleNetwork from './components/canvas/ParticleNetwork';
import Header from './components/layout/Header';
import ScrollProgress from './components/layout/ScrollProgress';
import Hero from './components/sections/Hero';
import Services from './components/sections/Services';
import Team from './components/sections/Team';
import About from './components/sections/About';
import Contact from './components/sections/Contact';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

/* -------------------------------------------------------
   Preloader
   ------------------------------------------------------- */
function Preloader({ onComplete }) {
  const preloaderRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    const tl = gsap.timeline({
      onComplete: () => {
        gsap.to(preloaderRef.current, {
          opacity: 0,
          duration: 0.6,
          ease: 'power2.inOut',
          onComplete,
        });
      },
    });

    tl.fromTo(
      textRef.current,
      { opacity: 0, scale: 0.8 },
      { opacity: 1, scale: 1, duration: 0.8, ease: 'power3.out' }
    ).to(textRef.current, {
      opacity: 0,
      scale: 1.1,
      duration: 0.5,
      delay: 0.5,
      ease: 'power2.in',
    });
  }, [onComplete]);

  return (
    <div
      ref={preloaderRef}
      className="preloader"
    >
      <div ref={textRef} className="text-center">
        <div className="font-display font-medium text-3xl text-white tracking-[0.2em]">
          EURISTIK
        </div>
        <div className="mt-4 flex items-center gap-1 justify-center">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-white/40"
              style={{
                animation: `pulse 1s ease-in-out ${i * 0.15}s infinite`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------
   Custom Cursor (desktop only)
   ------------------------------------------------------- */
function CustomCursor() {
  const cursorRef = useRef(null);

  useEffect(() => {
    if (!window.matchMedia('(pointer: fine)').matches) return;

    const cursor = cursorRef.current;
    if (!cursor) return;

    // Target position (raw mouse) and current position (lerped)
    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };
    const ease = 0.12; // lower = smoother/slower, higher = snappier
    let raf;

    const onMove = (e) => {
      target.x = e.clientX - 10;
      target.y = e.clientY - 10;
    };

    const animate = () => {
      current.x += (target.x - current.x) * ease;
      current.y += (target.y - current.y) * ease;
      cursor.style.transform = `translate3d(${current.x}px, ${current.y}px, 0)`;
      raf = requestAnimationFrame(animate);
    };

    // Position cursor via transform instead of left/top (GPU-accelerated)
    cursor.style.left = '0px';
    cursor.style.top = '0px';
    cursor.style.willChange = 'transform';

    document.addEventListener('mousemove', onMove);
    raf = requestAnimationFrame(animate);

    const addHover = () => cursor.classList.add('hovering');
    const removeHover = () => cursor.classList.remove('hovering');

    const interactives = document.querySelectorAll('a, button, input, textarea');
    interactives.forEach((el) => {
      el.addEventListener('mouseenter', addHover);
      el.addEventListener('mouseleave', removeHover);
    });

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousemove', onMove);
      interactives.forEach((el) => {
        el.removeEventListener('mouseenter', addHover);
        el.removeEventListener('mouseleave', removeHover);
      });
    };
  }, []);

  return <div ref={cursorRef} className="custom-cursor hidden md:block" />;
}

/* -------------------------------------------------------
   App
   ------------------------------------------------------- */
/* -------------------------------------------------------
   Section IDs in order (for snap navigation)
   ------------------------------------------------------- */
const SECTIONS = ['#hero', '#services', '#team', '#about', '#contact'];

export default function App() {
  const [loaded, setLoaded] = useState(false);
  const scrollProgressRef = useRef({ current: 0 });
  const currentSectionRef = useRef(0);
  const isAnimatingRef = useRef(false);
  const contentRef = useRef(null);
  const scrollVelocityRef = useRef({ current: 0 });
  const mouseRef = useRef({ current: { x: 0, y: 0 } });
  const heroSlideRef = useRef({ current: 0 });

  const handlePreloaderComplete = useCallback(() => {
    setLoaded(true);
  }, []);

  const [displayProgress, setDisplayProgress] = useState(0);

  // Smooth lerp loop for 3D scene scroll value + mouse tracking
  useEffect(() => {
    let rawProgress = 0;
    let smoothProgress = 0;
    const ease = 0.05;
    let raf;

    // Mouse: raw target and smooth current (-1 to 1, centered)
    const rawMouse = { x: 0, y: 0 };
    const smoothMouse = { x: 0, y: 0 };
    const mouseEase = 0.06;

    const updateRaw = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      rawProgress = totalHeight > 0 ? window.scrollY / totalHeight : 0;
      setDisplayProgress(rawProgress);
    };

    const onMouseMove = (e) => {
      rawMouse.x = (e.clientX / window.innerWidth) * 2 - 1;   // -1 left, +1 right
      rawMouse.y = (e.clientY / window.innerHeight) * 2 - 1;  // -1 top, +1 bottom
    };


    let prevSmooth = 0;

    const animate = () => {
      smoothProgress += (rawProgress - smoothProgress) * ease;
      if (Math.abs(rawProgress - smoothProgress) < 0.0001) {
        smoothProgress = rawProgress;
      }
      scrollProgressRef.current.current = smoothProgress;

      // Velocity
      const velocity = Math.abs(smoothProgress - prevSmooth) * 120;
      scrollVelocityRef.current.current +=
        (velocity - scrollVelocityRef.current.current) * 0.15;
      prevSmooth = smoothProgress;

      // Smooth mouse
      smoothMouse.x += (rawMouse.x - smoothMouse.x) * mouseEase;
      smoothMouse.y += (rawMouse.y - smoothMouse.y) * mouseEase;
      mouseRef.current.current.x = smoothMouse.x;
      mouseRef.current.current.y = smoothMouse.y;

      raf = requestAnimationFrame(animate);
    };

    window.addEventListener('scroll', updateRaw, { passive: true });
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    raf = requestAnimationFrame(animate);
    updateRaw();

    return () => {
      window.removeEventListener('scroll', updateRaw);
      window.removeEventListener('mousemove', onMouseMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Snap-scroll: intercept wheel and navigate section by section
  useEffect(() => {
    // Detect which section is closest on load / after manual scroll
    const detectCurrentSection = () => {
      let closest = 0;
      let minDist = Infinity;
      SECTIONS.forEach((id, i) => {
        const el = document.querySelector(id);
        if (!el) return;
        const dist = Math.abs(el.getBoundingClientRect().top);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      currentSectionRef.current = closest;
    };

    const goToSection = (index) => {
      const clamped = Math.max(0, Math.min(index, SECTIONS.length - 1));
      if (clamped === currentSectionRef.current && isAnimatingRef.current) return;

      const target = document.querySelector(SECTIONS[clamped]);
      if (!target) return;

      isAnimatingRef.current = true;
      currentSectionRef.current = clamped;

      gsap.to(window, {
        scrollTo: { y: target, autoKill: false },
        duration: 1.2,
        ease: 'power3.inOut',
        onComplete: () => {
          isAnimatingRef.current = false;
        },
      });
    };

    // Wheel handler - snap on each wheel tick
    let wheelCooldown = false;
    const onWheel = (e) => {
      // Free scroll on mobile
      if (window.innerWidth < 768) return;
      // Allow normal scroll inside form fields
      if (e.target.closest('input, textarea, select')) return;

      e.preventDefault();

      if (isAnimatingRef.current || wheelCooldown) return;

      // Cooldown to avoid rapid-fire from trackpads
      wheelCooldown = true;
      setTimeout(() => { wheelCooldown = false; }, 800);

      const direction = e.deltaY > 0 ? 1 : -1;
      goToSection(currentSectionRef.current + direction);
    };

    // Touch handler for mobile swipe
    let touchStartY = 0;
    let touchStartX = 0;
    const onTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
      if (window.innerWidth < 768) return;
      if (isAnimatingRef.current) return;
      const deltaY = touchStartY - e.changedTouches[0].clientY;
      const deltaX = touchStartX - e.changedTouches[0].clientX;
      // If swipe is more horizontal than vertical, let the Hero carousel handle it
      if (Math.abs(deltaX) > Math.abs(deltaY)) return;
      if (Math.abs(deltaY) < 50) return; // ignore small swipes
      const direction = deltaY > 0 ? 1 : -1;
      goToSection(currentSectionRef.current + direction);
    };

    // Keyboard arrows / page up-down
    const onKeyDown = (e) => {
      if (isAnimatingRef.current) return;
      if (e.target.closest('input, textarea, select')) return;

      let direction = 0;
      if (e.key === 'ArrowDown' || e.key === 'PageDown') direction = 1;
      if (e.key === 'ArrowUp' || e.key === 'PageUp') direction = -1;
      if (direction !== 0) {
        e.preventDefault();
        goToSection(currentSectionRef.current + direction);
      }
    };

    detectCurrentSection();

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return (
    <>
      {!loaded && <Preloader onComplete={handlePreloaderComplete} />}

      <ParticleNetwork scrollProgress={scrollProgressRef.current} scrollVelocity={scrollVelocityRef.current} mouse={mouseRef.current} heroSlide={heroSlideRef.current} />

      <div ref={contentRef} className="content-overlay">
        <Header />
        <ScrollProgress progress={displayProgress} />

        <main>
          <Hero heroSlide={heroSlideRef.current} />
          <Services />
          <Team />
          <About />
          <Contact />
        </main>
      </div>

      <CustomCursor />
    </>
  );
}
